import { useQuery } from '@tanstack/react-query'
import { Activity } from 'lucide-react'

import type { ActivityEntry } from '../../api/types.gen'
import { api } from '../../api/client'
import { formatStudioDateTime } from '../../lib/deliveryDate'
import { Card, ICON_STROKE, Skeleton } from '../../ui'


/**
 * Qué han hecho los clientes con esta entrega.
 *
 * El texto de cada línea importa más que la línea. «Autorizó la descarga» y no
 * «descargó»: el byte va de R2 al navegador sin pasar por nosotros (ADR-01), así
 * que sabemos que se firmó la URL, no que llegara. Es una diferencia de matiz
 * hasta que un estudio enseña esta pantalla en una discusión con su cliente, y
 * entonces es la diferencia entre un acuse y una afirmación falsa.
 */
const LABEL: Record<string, (count: number | null) => string> = {
  'share.unlocked': () => 'Abrió la entrega',
  'share.download_authorized': (count) =>
    count === null || count === 1
      ? 'Autorizó la descarga de un archivo'
      : `Autorizó la descarga de ${count} archivos`,
  'share.bundle_requested': (count) =>
    count === null ? 'Pidió el ZIP' : `Pidió el ZIP de ${count} archivos`,
  'share.files_received': (count) =>
    count === null || count === 1 ? 'Te envió un archivo' : `Te envió ${count} archivos`,
}

export function DeliveryActivity({ projectId }: { projectId: string }) {
  const { data, isPending } = useQuery({
    queryKey: ['project-activity', projectId],
    queryFn: () => api.get<{ data: ActivityEntry[] }>(`/projects/${projectId}/activity?limit=50`),
    enabled: projectId !== '',
  })

  if (isPending) return <Skeleton className="h-24 w-full rounded-card" />

  const entries = data?.data ?? []

  // Sin actividad no se pinta nada. Una entrega recién creada no tiene por qué
  // enseñar un bloque vacío diciendo que nadie la ha abierto todavía: eso lo
  // sabe el estudio, que la acaba de crear.
  if (entries.length === 0) return null

  return (
    <Card className="p-4">
      <h2 className="flex items-center gap-2 text-subhead font-semibold text-ink">
        <Activity size={18} strokeWidth={ICON_STROKE} aria-hidden />
        Actividad de tus clientes
      </h2>

      <ul className="mt-4 flex flex-col divide-y divide-line">
        {entries.map((entry, index) => (
          <li key={`${entry.at}-${index}`} className="py-2.5 first:pt-0 last:pb-0">
            <p className="text-body text-ink">{LABEL[entry.action]?.(entry.count) ?? entry.action}</p>
            <p className="text-label text-ink-subtle" data-numeric>
              {formatStudioDateTime(entry.at)} · por «{entry.link}»
              {entry.from !== null && <> · desde {entry.from}</>}
            </p>
          </li>
        ))}
      </ul>

      {/*
        Se dice el plazo aquí y no sólo en la política de privacidad. El estudio
        que se apoye en esto para una reclamación tiene que saber que no estará
        para siempre, ANTES de necesitarlo.
      */}
      <p className="mt-4 border-t border-line pt-3 text-label text-ink-subtle">
        Se guarda durante 180 días.
      </p>
    </Card>
  )
}
